/**
 * Shared request shapes for navigation and interaction tracking.
 *
 * These live apart from the two tracking services so TrackingDispatchService can reference them
 * without importing the services that delegate to it. Both services re-export everything here, so
 * existing call sites keep importing InteractionType and friends from where they always did.
 */

/** Mirrors BrainChanger.Domain.Models.Enum.InteractionType. */
export enum InteractionType {
  Add = 1,
  Edit = 2,
  Delete = 3,
  Selection = 4,
  ClearAll = 5,
  Confirm = 6,
  Cancel = 7,
  Start = 8,
  Complete = 9,
}

/** Mirrors BrainChanger.Domain.Models.Enum.NavigationTriggerType. */
export enum NavigationTriggerType {
  ViaNavigation = 1,
  InPageNav = 2,
  /** FLIP-212: written by the router when no explicit track() call covered the route change. */
  RouteChange = 3,
}

export interface InteractionEventRequest {
  screen: string;
  eventName: string;
  interactionType: InteractionType;
  detail?: string;

  /**
   * The record this event acted on, e.g. "Lesson" / 17. Lets one row's whole edit history be
   * followed, which Detail alone cannot support — two edits to two different mini safety plans
   * are otherwise indistinguishable.
   */
  entityType?: string;
  entityId?: number;

  /**
   * Groups every event produced by ONE user action. A single save that changes three cards is one
   * intent, not three; without this the three rows read as three separate edits.
   */
  correlationId?: string;

  /**
   * The value before this edit, same "marker: value" convention as detail.
   *
   * STRUCTURED FIELDS ONLY — cards, categories, durations, tags, positions. For free text (notes
   * to self, mini-plan if-statements, star/shadow labels) record only that an edit happened and
   * leave the canonical row as the single copy of what the person wrote.
   */
  previousDetail?: string;
}

export interface NavigationEventRequest {
  fromScreen: string;
  toScreen: string;
  triggerType: NavigationTriggerType;
  eventName: string;

  /** Ties this navigation to the interaction that triggered it. See the interaction shape above. */
  correlationId?: string;
}

/**
 * Live-stream shape emitted on `TrackingDispatchService.events$` (FLIP-298). Covers both
 * interaction and navigation events, so it omits fields navigation events don't have (entityType,
 * entityId) rather than union-ing the two source shapes.
 */
export interface TrackedEvent {
  eventName: string;
  entityType?: string;
  entityId?: number;
  correlationId?: string;
  timestamp: number;
}
